import { useCallback, useEffect, useState } from 'react';
import { IntlShape } from 'react-intl';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { updateAlertsCount, updateR2SLink, updateWellInfo } from 'redux/WellGuardian';
import { useCountryState, useSelectedSite, useSelectWellInfo } from 'redux/WellGuardian/selectors';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';

const WELL_LINK_TYPE = 'well';

const SITE_LINK_TYPE = 'platform';

export const useFetchWgHeaderData = (
  tuwi: string,
  intl: IntlShape,
  reloadAlertsCount?: boolean,
) => {
  const [wellInfoLoading, setWellInfoLoading] = useState<boolean>(false);
  const [alertsCountLoading, setAlertsCountLoading] = useState<boolean>(false);
  const [r2SLinkLoading, setR2SLinkLoading] = useState<boolean>(false);

  const countryState = useCountryState();
  const wellInfo = useSelectWellInfo();
  const site = useSelectedSite();
  const dispatch = useDispatch();

  const fetchWellInfo = useCallback(async () => {
    setWellInfoLoading(true);
    const { response, error } = await wellGuardianApiClient.getWellInfoByTuwi(tuwi, countryState);
    if (error) {
      toast.error(intl.formatMessage({ id: 'wellGuardian.header.errorLoadingWellInfo' }));
      setWellInfoLoading(false);

      return;
    }
    dispatch(updateWellInfo(response));
    setWellInfoLoading(false);
  }, [tuwi, countryState, dispatch, intl]);

  const fetchAlertsCount = useCallback(async () => {
    setAlertsCountLoading(true);
    const { response, error } = await wellGuardianApiClient.getAlertsCountByTuwi(tuwi);
    if (!error) {
      dispatch(updateAlertsCount(response));
    }
    setAlertsCountLoading(false);
  }, [tuwi, dispatch]);

  const fetchR2SLink = useCallback(async () => {
    setR2SLinkLoading(true);
    const r2SLink = tuwi
      ? await wellGuardianApiClient.getR2SLink(tuwi, WELL_LINK_TYPE)
      : await wellGuardianApiClient.getR2SLink(site, SITE_LINK_TYPE);
    dispatch(updateR2SLink(r2SLink ?? null));
    setR2SLinkLoading(false);
  }, [tuwi, site, dispatch]);

  useEffect(() => {
    if (tuwi && wellInfo?.wellViewId !== tuwi && countryState) {
      fetchWellInfo();
      fetchAlertsCount();
      fetchR2SLink();
    } else if (!tuwi && countryState) {
      fetchR2SLink();
    }
  }, [wellInfo, tuwi, fetchAlertsCount, fetchWellInfo, fetchR2SLink, countryState]);

  useEffect(() => {
    if (reloadAlertsCount) {
      fetchAlertsCount();
    }
  }, [reloadAlertsCount, fetchAlertsCount]);

  return {
    alertsCountLoading,
    wellInfoLoading,
    wellInfo,
    r2SLinkLoading,
  };
};
